import { resmgrHostsSelector } from 'app/plugins/openstack/components/infrastructure/selectors'
import { useAppSelector } from 'app/store'
import ListContainer from 'core/containers/ListContainer'
import { createGridStatusCell } from 'core/elements/grid/cells/GridStatusCell'
import Tooltip from 'core/elements/tooltip'
import useListAction from 'core/hooks/useListAction'
import React, { useCallback, useMemo } from 'react'
import { useSelector } from 'react-redux'
import { listResMgrHosts } from '../../resMgrHosts/actions'
import {
  listComputeServiceDetails,
  listImageServiceDetails,
  listNetworkServiceDetails,
  listStorageServiceDetails,
} from './action'
import { ServiceType } from './constant'
import { HealthStatusBadge } from './HealthStatusBadge'
import {
  IPAddressCellComponent,
  buildCompleteCombinedServices,
  getConnectivityStatus,
  validServiceDetails,
} from './helper'
import { combinedServicesSelector } from './selector'
import { createUsePrefParamsHook } from 'core/hooks/useParams'
import { listTablePrefs, TablePrefsParams } from 'app/constants'
import { pick } from 'ramda'
import { getProductName } from 'core/utils/helpers'

const hostColumns = [
  {
    key: 'name',
    label: 'Host Name',
  },
  {
    key: 'clusterName',
    label: 'Cluster',
  },
  {
    key: 'connectivityStatus',
    label: (
      <Tooltip
        message={`Reports the status of the connection between ${getProductName()} management plane and the host`}
      >
        <span>Connectivity Status</span>
      </Tooltip>
    ),
    CellComponent: createGridStatusCell({
      dataFn: (phase: string) => getConnectivityStatus(phase),
    }),
  },
  {
    key: 'computeStatus',
    label: 'Compute Service',
    CellComponent: ({ value, item }) => (
      <HealthStatusBadge value={value} serviceType={ServiceType.Compute} />
    ),
  },
  {
    key: 'imageStatus',
    label: 'Image Service',
    CellComponent: ({ value, item }) => (
      <HealthStatusBadge value={value} serviceType={ServiceType.Image} endpoint={item?.endpoint} />
    ),
  },
  {
    key: 'storageStatus',
    label: 'Persistent Storage Service',
    CellComponent: ({ value }) => (
      <HealthStatusBadge value={value} serviceType={ServiceType.Storage} />
    ),
  },
  {
    key: 'networkStatus',
    label: 'Networking Service',
    CellComponent: ({ value }) => (
      <HealthStatusBadge value={value} serviceType={ServiceType.Network} />
    ),
  },
  {
    key: 'ipAddresses',
    label: 'IP Addresses',
    CellComponent: IPAddressCellComponent,
  },
]
type Params = {}
const usePrefParams = createUsePrefParamsHook<Params & TablePrefsParams>(
  'ResmgrHosts',
  listTablePrefs,
)

export default function ServiceHealthHostsListPage() {
  const { params, getParamsUpdater } = usePrefParams({})

  const { loading: loadingHosts, reload: reloadHosts } = useListAction(listResMgrHosts)
  const { loading: loadingComputeService, reload: reloadComputeService } = useListAction(
    listComputeServiceDetails,
  )
  const { loading: loadingStorageService, reload: reloadStorageService } = useListAction(
    listStorageServiceDetails,
  )
  const { loading: loadingNetworkService, reload: reloadNetworkService } = useListAction(
    listNetworkServiceDetails,
  )
  const { loading: imageLoading, reload: reloadImageService } = useListAction(
    listImageServiceDetails,
  )
  const combinedServicesDetails = useAppSelector(combinedServicesSelector)
  const hosts = useSelector(resmgrHostsSelector)

  // Filter out services that do not have host details
  const validCombinedServicesDetails = useMemo(() => {
    return validServiceDetails(combinedServicesDetails)
  }, [combinedServicesDetails])

  const completeCombinedServices = useMemo(
    () => buildCompleteCombinedServices(validCombinedServicesDetails, hosts),
    [validCombinedServicesDetails, hosts],
  )

  const handleRefresh = useCallback(() => {
    reloadComputeService(true)
    reloadStorageService(true)
    reloadNetworkService(true)
    reloadImageService(true)
    reloadHosts(true)
  }, [reloadHosts, reloadComputeService, reloadStorageService, reloadNetworkService])

  return (
    <>
      <ListContainer<any, any>
        uniqueIdentifier={'name'}
        label="Host Health"
        searchTargets={['name', 'clusterName']}
        loading={
          loadingHosts ||
          loadingComputeService ||
          loadingStorageService ||
          loadingNetworkService ||
          imageLoading
        }
        loadingMessage={'Loading...'}
        onRefresh={handleRefresh}
        data={completeCombinedServices}
        columns={hostColumns}
        emptyContent="No hosts found"
        hideCheckboxes
        showBreadcrumbs={false}
        {...pick(listTablePrefs, params)}
        getParamsUpdater={getParamsUpdater}
      />
    </>
  )
}
